import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  DatePicker,
  Checkbox,
  InputNumber,
  message,
} from 'antd'
import moment from 'moment'
import { ProductSelector } from '../../components/product-selector'
import { sleep } from '@guillotinaweb/react-gmi'
import { useLocation, useRoute } from 'wouter'
import startOfToday from 'date-fns/startOfToday'

const { TextArea } = Input

// Función auxiliar para formatear el campaign_name
function formatCampaignName(name) {
  return name.trim().replace(/\s+/g, '_')
}

// Función para extraer el nombre de la campaña sin la fecha
function extractCampaignName(fullName) {
  // Busca el patrón de fecha DD_MM_YY_ al inicio del nombre
  const datePattern = /^\d{2}_\d{2}_\d{2}_/
  return fullName.replace(datePattern, '')
}

export default function AddNewContent() {
  const [showFeatureOptions, setShowFeatureOptions] = useState(false)
  const handleFeatureBadgeChange = (e) => {
    setShowFeatureOptions(e.target.checked)
  }
  const [showSelectProduct, setSelectProducts] = useState(true)
  const handleCampanaSinProductos = (e) => {
    if (e.target.checked === true) {
      setSelectProducts(false)
    } else {
      setSelectProducts(true)
    }
  }
  const [idsProducts, setIdsProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [location, setLocation] = useLocation()
  const [, params] = useRoute('/content_ai/edit_content/:id')
  const id = params?.id
  const isEditMode = !!id
  const [form] = Form.useForm()
  const [campaignType, setCampaignType] = useState('')
  useEffect(() => {
    if (isEditMode) {
      loadContentData()
    }
  }, [id])

  async function loadContentData() {
    try {
      setLoading(true)
      const contentData = JSON.parse(sessionStorage.getItem('editContent'))

      if (!contentData) {
        throw new Error('No se encontraron los datos de la campaña')
      }

      // Extraer el nombre de la campaña sin la fecha
      const cleanCampaignName = extractCampaignName(contentData.campaign_name)

      const dates = [
        moment(contentData.start_date),
        moment(contentData.end_date),
      ]

      if (contentData.promoted_products) {
        let products = contentData.promoted_products
        if (typeof products === 'string') {
          products = products.split(' ').filter((p) => p !== '')
        }
        setIdsProducts([...products, ''])
      }

      setCampaignType(contentData.type)

      setShowFeatureOptions(contentData.feature_badge)

      const hasSinProductos =
        !contentData.promoted_products ||
        contentData.promoted_products.length === 0 ||
        contentData.promoted_products[0] === ''
      setSelectProducts(!hasSinProductos)

      form.setFieldsValue({
        campaign_name: cleanCampaignName.replace(/_/g, ' '),
        title: contentData.title,
        type: contentData.type,
        description: contentData.description,
        topic: contentData.topic,
        date: dates,
        promotion_topic: contentData.promotion_topic,
        marketing_text: contentData.marketing_text,
        feature_badge: contentData.feature_badge,
        feature_value: contentData.feature_value,
        feature_text: contentData.feature_text,
        landing_page_url: contentData.landing_page_url,
        sin_productos: hasSinProductos,
        oferta_producto: contentData.oferta_producto,
      })
    } catch (error) {
      message.error('Error al cargar los datos de la campaña: ' + error.message)
      setLocation('/content_ai/content_generated')
    } finally {
      setLoading(false)
    }
  }

  function addNewProduct(productKey, index) {
    setIdsProducts((prevIdsProducts) => {
      const newIdsProducts = [...prevIdsProducts]
      if (productKey && productKey[0]) {
        newIdsProducts[index] = productKey[0]
      } else {
        newIdsProducts.splice(index, 1)
      }
      if (
        newIdsProducts.length === 0 ||
        newIdsProducts[newIdsProducts.length - 1] !== ''
      ) {
        newIdsProducts.push('')
      }
      console.log('Updated products:', newIdsProducts)
      return newIdsProducts
    })
  }

  async function onFinish(values) {
    setLoading(true)
    try {
      // Formateamos el campaign_name (solo espacios a guiones bajos)
      values.campaign_name = formatCampaignName(values.campaign_name)

      if (
        !values.sin_productos &&
        (!idsProducts ||
          idsProducts.length === 0 ||
          (idsProducts.length === 1 && idsProducts[0] === ''))
      ) {
        throw new Error('No hay productos seleccionados')
      }

      if (values.date && values.date.length === 2) {
        values.date = [
          moment(values.date[0]).format('YYYY-MM-DDTHH:mm:ss'),
          moment(values.date[1]).format('YYYY-MM-DDTHH:mm:ss'),
        ]
      }

      if (values.sin_productos) {
        values['promoted_products'] = ['']
      } else {
        values['promoted_products'] = idsProducts.filter((id) => id !== '')
      }

      console.log('Valores a enviar:', values)
      console.log('Products:', values.promoted_products)

      const endpoint = isEditMode
        ? `/db/guillotina/@update-content/${id}`
        : `/db/guillotina/@new-content`

      const res = await fetch(endpoint, {
        method: isEditMode ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      })

      if (res.status === 200) {
        message.success(
          isEditMode ? 'Contenido actualizado' : 'Contenido creado'
        )
        if (isEditMode) {
          sessionStorage.removeItem('editContent')
        }
        await sleep(2000)
        setLocation('/content_ai/content_generated')
      } else {
        const response = await res.json()
        throw new Error(response)
      }
    } catch (error) {
      message.error(`Hubo un error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="container"
      style={{ backgroundColor: '#fff', padding: '30px' }}
    >
      <div style={{ marginTop: '10px' }}>
        <div className="field">
          <label className="label">
            {isEditMode ? 'Editar campaña' : 'Crear nueva campaña'}
          </label>
        </div>
        <Form
          form={form}
          name="campaign_form"
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 16,
          }}
          style={{
            maxWidth: 600,
          }}
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{
            campaign_name: '',
            title: '',
            type: '',
            description: '',
            topic: '',
            promotion_topic: '',
            marketing_text: '',
            feature_badge: false,
            feature_value: null,
            feature_text: '',
            landing_page_url: '',
            oferta_producto: true,
            remember: true,
          }}
        >
          <Form.Item
            label="Campaign name"
            name="campaign_name"
            rules={[
              {
                required: true,
                message: 'Por favor introduce un campaign name',
              },
              {
                pattern: /^[a-zA-Z0-9\s]+$/,
                message: 'Solo se permiten letras (sin ñ), números y espacios',
              },
            ]}
          >
            <Input
              onChange={(e) => {
                const invalidChars = e.target.value.match(/[^a-zA-Z0-9\s]/g)
                if (invalidChars) {
                  message.warning(
                    `Caracter no permitido: "${invalidChars.join(
                      ', '
                    )}" - Solo se permiten letras (sin ñ), números y espacios`
                  )
                }
                const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '')
                form.setFieldsValue({ campaign_name: value })
              }}
            />
          </Form.Item>
          <Form.Item
            label="Titulo campaña"
            name="title"
            rules={[
              {
                required: true,
                message: 'Por favor introduce un titulo',
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Tipo campaña"
            name="type"
            rules={[
              {
                required: true,
                message: 'Por favor selecciona un tipo de campaña',
              },
            ]}
          >
            <Select
              id="type"
              options={[
                {
                  label: 'Collection',
                  value: 'collection',
                },
                {
                  label: 'Product',
                  value: 'product',
                },
              ]}
              style={{
                width: 300,
              }}
              onChange={(value) => setCampaignType(value)}
            />
          </Form.Item>
          {campaignType === 'product' ? (
            <Form.Item
              label="Oferta producto"
              name="oferta_producto"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
          ) : null}
          <Form.Item label="Descripcion" name="description">
            <TextArea />
          </Form.Item>
          <Form.Item label="Topic" name="topic">
            <TextArea />
          </Form.Item>
          <Form.Item
            label="Fecha campaña"
            name="date"
            rules={[
              { required: true, message: 'Please input the duration' },
              () => ({
                validator(rule, value) {
                  if (
                    !value ||
                    new Date(value[0].toISOString()).getTime() >=
                      startOfToday().getTime()
                  ) {
                    return Promise.resolve()
                  }
                  return Promise.reject(`The offer shouldn't be expired`)
                },
              }),
            ]}
          >
            <DatePicker.RangePicker
              showTime={{
                defaultValue: [
                  moment('00:00:00', 'HH:mm:ss'),
                  moment('23:59:59', 'HH:mm:ss'),
                ],
              }}
            />
          </Form.Item>
          <Form.Item label="Promotion topic" name="promotion_topic">
            <Input />
          </Form.Item>
          <Form.Item
            label="Campaña sin productos"
            name="sin_productos"
            valuePropName="checked"
          >
            <Checkbox onChange={handleCampanaSinProductos} />
          </Form.Item>
          {showSelectProduct ? (
            <Form.Item label="Select products" value="promoted_products">
              {idsProducts.map((productKey, index) => (
                <ProductSelector
                  key={index}
                  products={productKey ? productKey : []}
                  type="refs"
                  state={['new_product', 'selling', 'notavailable']}
                  closable={false}
                  placeholder="Add a new product"
                  onChange={(product) => addNewProduct(product, index)}
                />
              ))}
              {idsProducts.length === 0 ||
              idsProducts[idsProducts.length - 1] !== '' ? (
                <ProductSelector
                  products={[]}
                  type="refs"
                  state={['new_product', 'selling', 'notavailable']}
                  closable={false}
                  placeholder="Add a new product"
                  onChange={(product) =>
                    addNewProduct(product, idsProducts.length)
                  }
                />
              ) : null}
            </Form.Item>
          ) : null}
          <Form.Item label="Marketing text" name="marketing_text">
            <Input />
          </Form.Item>
          <Form.Item
            label="Feature badge"
            name="feature_badge"
            valuePropName="checked"
          >
            <Checkbox onChange={handleFeatureBadgeChange} />
          </Form.Item>
          {showFeatureOptions && (
            <>
              <Form.Item label="Feature value" name="feature_value">
                <InputNumber />
              </Form.Item>
              <Form.Item label="Feature text" name="feature_text">
                <Input />
              </Form.Item>
            </>
          )}
          <Form.Item label="Landing page url" name="landing_page_url">
            <Input />
          </Form.Item>
          <Form.Item
            wrapperCol={{
              offset: 8,
              span: 16,
            }}
          >
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditMode ? 'Actualizar' : 'Crear'}
            </Button>
            <Button
              style={{ marginLeft: '10px' }}
              onClick={() => setLocation('/content_ai/content_generated')}
            >
              Cancelar
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  )
}
